import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Brain, Trophy, Timer, Eye } from 'lucide-react';
import playerManager from '../../utils/playerManager';

const MemoryPuzzle = ({ onGameComplete, gameConfig }) => {
  const [gameState, setGameState] = useState('waiting'); // waiting, showing, memorizing, playing, finished
  const [sequence, setSequence] = useState([]);
  const [playerSequence, setPlayerSequence] = useState([]);
  const [currentRound, setCurrentRound] = useState(1);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(gameConfig.duration);
  const [showingIndex, setShowingIndex] = useState(-1);
  const [wrongAnswer, setWrongAnswer] = useState(false);
  
  const colors = [
    { id: 0, name: 'Red', bg: 'bg-red-500', active: 'bg-red-300' },
    { id: 1, name: 'Blue', bg: 'bg-blue-500', active: 'bg-blue-300' },
    { id: 2, name: 'Green', bg: 'bg-green-500', active: 'bg-green-300' },
    { id: 3, name: 'Yellow', bg: 'bg-yellow-500', active: 'bg-yellow-300' },
    { id: 4, name: 'Purple', bg: 'bg-purple-500', active: 'bg-purple-300' },
    { id: 5, name: 'Pink', bg: 'bg-pink-500', active: 'bg-pink-300' }
  ];
  
  // Generate new sequence for round
  const generateSequence = () => {
    const newSequence = [];
    const sequenceLength = Math.min(2 + currentRound, 8); // Start with 3, max 8
    
    for (let i = 0; i < sequenceLength; i++) {
      newSequence.push(Math.floor(Math.random() * colors.length));
    }
    
    setSequence(newSequence);
    return newSequence;
  };
  
  // Show sequence to player
  const showSequence = async (seq) => {
    setGameState('showing');
    setShowingIndex(-1);
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    for (let i = 0; i < seq.length; i++) {
      setShowingIndex(seq[i]);
      await new Promise(resolve => setTimeout(resolve, 600));
      setShowingIndex(-1);
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    
    setGameState('playing');
  };
  
  // Start game
  const startGame = () => {
    setGameState('memorizing');
    setScore(0);
    setTimeLeft(gameConfig.duration);
    setCurrentRound(1);
    setPlayerSequence([]);
    setWrongAnswer(false);
    
    const newSequence = generateSequence();
    showSequence(newSequence);
  };
  
  // Game timer
  useEffect(() => {
    if ((gameState === 'playing' || gameState === 'showing') && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(time => time - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      endGame();
    }
  }, [gameState, timeLeft]);
  
  // Handle color click
  const handleColorClick = (colorId) => {
    if (gameState !== 'playing') return;
    
    const newPlayerSequence = [...playerSequence, colorId];
    setPlayerSequence(newPlayerSequence);
    
    // Check if current input is correct
    if (sequence[playerSequence.length] !== colorId) {
      setWrongAnswer(true);
      setTimeout(endGame, 1000);
      return;
    }
    
    // Check if sequence is complete
    if (newPlayerSequence.length === sequence.length) {
      // Round complete!
      const roundScore = sequence.length * 100 + (timeLeft * 5); // Bonus for time remaining
      setScore(prev => prev + roundScore);
      
      if (currentRound >= 5) {
        // Game complete after 5 rounds
        endGame();
      } else {
        // Next round
        setTimeout(() => {
          setCurrentRound(prev => prev + 1);
          setPlayerSequence([]);
          const newSequence = generateSequence();
          showSequence(newSequence);
        }, 1500);
      }
    }
  };
  
  // End game
  const endGame = async () => {
    setGameState('finished');
    
    const gameData = {
      roundsCompleted: currentRound - (wrongAnswer ? 1 : 0),
      longestSequence: Math.min(2 + currentRound - 1, 8),
      finalScore: score,
      timeRemaining: timeLeft,
      completion_time: (gameConfig.duration - timeLeft) * 1000
    };
    
    // Submit score to backend
    try {
      await playerManager.submitScore('memory-puzzle', 'Memory Master', score, gameData);
    } catch (error) {
      console.error('Failed to submit score:', error);
    }
    
    setTimeout(() => {
      onGameComplete(score, gameData);
    }, 3000);
  };
  
  if (gameState === 'waiting') {
    return (
      <div className="text-center">
        <Card className="gaming-card max-w-lg mx-auto p-8">
          <CardHeader className="pb-6">
            <Brain className="w-16 h-16 text-purple-400 mx-auto mb-4" />
            <CardTitle className="text-2xl text-white">Memory Master</CardTitle>
            <p className="text-gray-400">
              Watch the sequence of colors and repeat it exactly. Sequences get longer each round!
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Duration:</span>
                <span className="text-white">{gameConfig.duration} seconds</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Rounds:</span>
                <span className="text-white">5 rounds (sequences get longer)</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Entry Fee:</span>
                <span className="text-yellow-400 font-semibold">₹10</span>
              </div>
            </div>
            <Button onClick={startGame} className="w-full btn-primary py-3 text-lg">
              Start Game
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (gameState === 'finished') {
    const roundsCompleted = currentRound - (wrongAnswer ? 1 : 0);
    
    return (
      <div className="text-center">
        <Card className="gaming-card max-w-lg mx-auto p-8">
          <CardHeader className="pb-6">
            <Trophy className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <CardTitle className="text-2xl text-white">
              {wrongAnswer ? 'Game Over!' : 'Perfect Memory!'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 mb-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-yellow-400 mb-2">{score}</div>
                <div className="text-gray-400">Final Score</div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-purple-400">{roundsCompleted}</div>
                  <div className="text-gray-400 text-sm">Rounds Completed</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-400">{Math.min(2 + roundsCompleted, 8)}</div>
                  <div className="text-gray-400 text-sm">Longest Sequence</div>
                </div>
              </div>
            </div>
            
            <div className="text-gray-400 text-sm">
              {wrongAnswer ? 'Better luck next time!' : 'Amazing memory skills!'}<br/>
              Returning to games lobby in 3 seconds...
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="max-w-2xl mx-auto">
      {/* Game HUD */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex space-x-4">
          <Badge className="bg-yellow-400/20 text-yellow-400 border-yellow-400/30 px-4 py-2">
            Score: {score}
          </Badge>
          <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 px-4 py-2">
            Round: {currentRound}/5
          </Badge>
        </div>
        <Badge className="bg-red-500/20 text-red-400 border-red-500/30 px-4 py-2 flex items-center space-x-2">
          <Timer className="w-4 h-4" />
          <span>{timeLeft}s</span>
        </Badge>
      </div>
      
      {/* Game Status */}
      <div className="text-center mb-6">
        {gameState === 'showing' && (
          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 px-4 py-2 flex items-center space-x-2 justify-center w-fit mx-auto">
            <Eye className="w-4 h-4" />
            <span>Watch the sequence...</span>
          </Badge>
        )}
        {gameState === 'playing' && (
          <Badge className="bg-green-500/20 text-green-400 border-green-500/30 px-4 py-2 flex items-center space-x-2 justify-center w-fit mx-auto">
            <Brain className="w-4 h-4" />
            <span>Repeat the sequence ({playerSequence.length}/{sequence.length})</span>
          </Badge>
        )}
      </div>
      
      {/* Color Grid */}
      <Card className="gaming-card p-8">
        <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
          {colors.map((color) => (
            <button
              key={color.id}
              onClick={() => handleColorClick(color.id)}
              disabled={gameState !== 'playing'}
              className={`
                aspect-square rounded-lg transition-all duration-200 flex items-center justify-center text-white font-bold text-lg
                ${showingIndex === color.id ? color.active : color.bg}
                ${gameState === 'playing' ? 'hover:scale-105 active:scale-95' : ''}
                ${gameState !== 'playing' ? 'opacity-50' : ''}
                ${wrongAnswer && playerSequence[playerSequence.length - 1] === color.id ? 'ring-4 ring-red-500' : ''}
              `}
            >
              {color.name[0]}
            </button>
          ))}
        </div>
      </Card>
      
      {/* Sequence Progress */}
      {gameState === 'playing' && (
        <div className="mt-4">
          <div className="text-center text-gray-400 text-sm mb-2">Sequence Progress</div>
          <div className="flex justify-center space-x-1">
            {sequence.map((colorId, index) => (
              <div
                key={index}
                className={`w-4 h-4 rounded ${
                  index < playerSequence.length 
                    ? colors[colorId].bg
                    : 'bg-gray-600'
                }`}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Game Instructions */}
      <div className="mt-4 text-center text-gray-400 text-sm">
        {gameState === 'showing' ? 'Memorize the sequence of colors...' :
         gameState === 'playing' ? 'Click the colors in the same order you saw them' :
         'Get ready...'}
      </div>
    </div>
  );
};

export default MemoryPuzzle;